import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import {
  CheckingTypeEnum,
  InvestmentOrderAuthorizationResponse,
  InvestmentOrderRequest,
  LoadingStatus,
  NewInvestmentOrderStatus,
  ValidationViolation
} from '@qvs/core';
import * as fromActions from './new-investment-order.actions';
import { immerOn } from 'ngrx-immer/store';
import { selectAccountsThatAllowTrading } from '~/modules/accounts/slice';
import { selectCurrentPortfolioInstrumentsWhichAllowTrading } from '~/modules/portfolio/slice';
import { selectCurrentInstrumentId } from '~/modules/instrument/slice';

export interface NewInvestmentOrderState {
  creation: {
    investmentOrderRequest: InvestmentOrderRequest;
    violations: ValidationViolation[];
    orderId: number;
    allowAllPortfolios: boolean;
    loadingStatus: LoadingStatus;
  };
  authorization: {
    code: number;
    loadingStatus: LoadingStatus;
  };
  confirmation: {
    authorizationResponse: InvestmentOrderAuthorizationResponse;
    loadingStatus: LoadingStatus;
  };
  status: NewInvestmentOrderStatus;
}

export const initialState: NewInvestmentOrderState = {
  creation: {
    investmentOrderRequest: {} as InvestmentOrderRequest,
    violations: [] as ValidationViolation[],
    orderId: undefined as number,
    allowAllPortfolios: undefined,
    loadingStatus: LoadingStatus.IDLE
  },
  authorization: {
    code: undefined as number,
    loadingStatus: LoadingStatus.IDLE
  },
  confirmation: {
    authorizationResponse: {} as InvestmentOrderAuthorizationResponse,
    loadingStatus: LoadingStatus.IDLE
  },
  status: NewInvestmentOrderStatus.IDLE
};

export type State = NewInvestmentOrderState;

const reducer = createReducer(
  initialState,
  immerOn<State, [typeof fromActions.updateInvestmentOrderRequest]>(fromActions.updateInvestmentOrderRequest, (state, action) => {
    state.creation.investmentOrderRequest = action.investmentOrderRequest;
    state.creation.violations = action.violations;
  }),
  immerOn<State, [typeof fromActions.createInvestmentOrder]>(fromActions.createInvestmentOrder, (state) => {
    state.creation.loadingStatus = LoadingStatus.LOADING;
    state.status = NewInvestmentOrderStatus.CREATING_ORDER;
  }),
  immerOn<State, [typeof fromActions.createInvestmentOrderSuccess]>(fromActions.createInvestmentOrderSuccess, (state, action) => {
    state.creation.loadingStatus = LoadingStatus.SUCCEEDED;
    state.creation.orderId = action.response.orderId;
    state.status = NewInvestmentOrderStatus.CREATING_ORDER_SUCCEEDED;
  }),
  immerOn<State, [typeof fromActions.createInvestmentOrderFailure]>(fromActions.createInvestmentOrderFailure, (state) => {
    state.creation.loadingStatus = LoadingStatus.FAILED;
    state.status = NewInvestmentOrderStatus.CREATING_ORDER_FAILED;
  }),
  immerOn<State, [typeof fromActions.authorizeInvestmentOrder]>(fromActions.authorizeInvestmentOrder, (state) => {
    state.authorization.loadingStatus = LoadingStatus.LOADING;
    state.status = NewInvestmentOrderStatus.AUTHORIZING_ORDER;
  }),
  immerOn<State, [typeof fromActions.authorizeInvestmentOrderSuccess]>(
    fromActions.authorizeInvestmentOrderSuccess,
    (state, action) => {
      state.authorization.loadingStatus = LoadingStatus.SUCCEEDED;
      state.authorization.code = action.response.authorizationCode;
      state.status = NewInvestmentOrderStatus.AUTHORIZING_ORDER_SUCCEEDED;
    }
  ),
  immerOn<State, [typeof fromActions.authorizeInvestmentOrderFailure]>(fromActions.authorizeInvestmentOrderFailure, (state) => {
    state.authorization.loadingStatus = LoadingStatus.FAILED;
    state.status = NewInvestmentOrderStatus.AUTHORIZING_ORDER_FAILED;
  }),
  immerOn<State, [typeof fromActions.cancelAuthorization]>(fromActions.cancelAuthorization, (state) => {
    state.creation.loadingStatus = LoadingStatus.IDLE;
    state.creation.orderId = undefined;
    state.status = NewInvestmentOrderStatus.IDLE;
  }),
  immerOn<State, [typeof fromActions.confirmInvestmentOrder]>(fromActions.confirmInvestmentOrder, (state) => {
    state.confirmation.loadingStatus = LoadingStatus.LOADING;
    state.status = NewInvestmentOrderStatus.CONFIRMING_ORDER;
  }),
  immerOn<State, [typeof fromActions.confirmInvestmentOrderSuccess]>(
    fromActions.confirmInvestmentOrderSuccess,
    (state, action) => {
      state.confirmation.loadingStatus = LoadingStatus.SUCCEEDED;
      state.confirmation.authorizationResponse = action.response.investOrderAuthorizationResponse;
      state.status = NewInvestmentOrderStatus.CONFIRMING_ORDER_SUCCEEDED;
    }
  ),
  immerOn<State, [typeof fromActions.confirmInvestmentOrderFailure]>(fromActions.confirmInvestmentOrderFailure, (state) => {
    state.confirmation.loadingStatus = LoadingStatus.FAILED;
    state.status = NewInvestmentOrderStatus.CONFIRMING_ORDER_FAILED;
  }),
  immerOn<State, [typeof fromActions.setOrderSide]>(fromActions.setOrderSide, (state, action) => {
    state.creation.investmentOrderRequest.orderSide = action.orderSide;
  }),
  immerOn<State, [typeof fromActions.setAccountId]>(fromActions.setAccountId, (state, action) => {
    state.creation.investmentOrderRequest.accountId = action.accountId;
  }),
  immerOn<State, [typeof fromActions.setAllowAllPortfolios]>(fromActions.setAllowAllPortfolios, (state, action) => {
    state.creation.allowAllPortfolios = action.allowAllPortfolios;
  }),
  on<State, [typeof fromActions.resetNewInvestmentOrderForm]>(fromActions.resetNewInvestmentOrderForm, () => ({
    ...initialState
  }))
);

export const newInvestmentOrderFeatureKey = 'newInvestmentOrders';

export const newInvestmentOrderFeature = createFeature({
  name: newInvestmentOrderFeatureKey,
  reducer
});

export const { selectCreation, selectAuthorization, selectNewInvestmentOrdersState } = newInvestmentOrderFeature;

export const selectCurrentInvestmentOrderRequest = createSelector(selectCreation, (state) => state.investmentOrderRequest);

export const selectCreationLoadingStatus = createSelector(selectCreation, (state) => state.loadingStatus);

export const selectCreationLoadingStatusError = createSelector(
  selectCreation,
  (state) => state.loadingStatus === LoadingStatus.FAILED
);

export const selectCreationLoadingStatusSuccess = createSelector(
  selectCreation,
  (state) => state.loadingStatus === LoadingStatus.SUCCEEDED
);

export const selectCreationViolations = createSelector(selectCreation, (state) => state.violations);

export const selectHasAccountViolations = createSelector(selectCreationViolations, (violations) =>
  !!violations
    ? violations.some(
        (violation) =>
          violation.checkingType === CheckingTypeEnum.SHORT_ACCOUNT ||
          violation.checkingType === CheckingTypeEnum.ACCOUNT_BALANCE_BLOCKED ||
          violation.checkingType === CheckingTypeEnum.ACCOUNT_BLOCKED
      )
    : false
);

export const selectHasAmountViolations = createSelector(selectCreationViolations, (violations) =>
  !!violations
    ? violations.some(
        (violation) =>
          violation.checkingType === CheckingTypeEnum.SHORT_SELL || violation.checkingType === CheckingTypeEnum.NUMBER_DECIMAL
      )
    : false
);

export const selectHasLimitViolations = createSelector(selectCreationViolations, (violations) =>
  !!violations ? violations.some((violation) => violation.checkingType === CheckingTypeEnum.LIMIT_PRICE) : false
);

export const selectHasLimitDateViolations = createSelector(selectCreationViolations, (violations) =>
  !!violations ? violations.some((violation) => violation.checkingType === CheckingTypeEnum.LIMIT_DATE) : false
);

export const selectCurrentInvestmentAuthorizationCode = createSelector(selectAuthorization, (state) => state.code);

export const selectOrderSide = createSelector(selectCurrentInvestmentOrderRequest, (orderRequest) => orderRequest.orderSide);

export const selectAccountId = createSelector(selectCurrentInvestmentOrderRequest, (orderRequest) => orderRequest.accountId);

export const selectAllowAllPortfolios = createSelector(selectCreation, (state) => state.allowAllPortfolios);

export const selectAccountWithPortfolio = createSelector(selectAccountId, selectAccountsThatAllowTrading, (accountId, accounts) =>
  accounts.find((account) => account.id === accountId)
);

export const selectStatus = createSelector(selectNewInvestmentOrdersState, (state) => state.status);

export const selectInstrumentHoldings = createSelector(
  selectCurrentInstrumentId,
  selectCurrentPortfolioInstrumentsWhichAllowTrading,
  (instrumentId, instruments) => instruments.find((instrument) => instrument.securityId === instrumentId)?.units
);
